import React, { createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react';
import { useLocation } from 'react-router-dom';
import Alert from '../components/base/Alert';
import { setGlobalAlertHandler } from '../config/queryClient';

// One page-level alert for the signed-in app, rendered once by <AlertOutlet>.
// Replaces the per-page `useState` alerts (which could show the same message
// in App, Navigation and the page at the same time). Modals and forms that
// want an alert scoped to themselves keep their own local state and pass it
// to data hooks via `options.setAlert`.
const AlertContext = createContext(null);

const HIDDEN = { type: 'alert', msg: '', visible: false };

export const AlertProvider = ({ children }) => {
    const [alert, setAlert] = useState(HIDDEN);
    const { pathname } = useLocation();
    // Number of in-page outlets (PageHeader) currently mounted. While one is
    // mounted the app-level outlet stays silent so the alert shows where the
    // per-page alerts used to: under the page title / store selector.
    const [pageOutlets, setPageOutlets] = useState(0);

    const clearAlert = useCallback(() => {
        setAlert((prev) => (prev.visible ? { ...prev, visible: false } : prev));
    }, []);

    const registerPageOutlet = useCallback(() => {
        setPageOutlets((n) => n + 1);
        return () => setPageOutlets((n) => n - 1);
    }, []);

    // Page-local alerts used to disappear with the page; keep that behaviour.
    useEffect(() => { clearAlert(); }, [pathname, clearAlert]);

    // react-query's cache onError falls back to this when a query has no
    // meta.setAlert of its own.
    useEffect(() => {
        setGlobalAlertHandler(setAlert);
        return () => setGlobalAlertHandler(null);
    }, []);

    const value = useMemo(
        () => ({ alert, setAlert, clearAlert, hasPageOutlet: pageOutlets > 0, registerPageOutlet }),
        [alert, clearAlert, pageOutlets, registerPageOutlet]
    );
    return <AlertContext.Provider value={value}>{children}</AlertContext.Provider>;
};

export const useAlert = () => {
    const ctx = useContext(AlertContext);
    if (!ctx) throw new Error('useAlert must be used inside AlertProvider');
    return ctx;
};

// Renders the shared alert (nothing when hidden).
//
// `inPage` outlets live inside a page (PageHeader) and take precedence: the
// app-level outlet renders nothing while any in-page outlet is mounted, so a
// message appears once, below the page title, as it did before the shared
// alert state (bug 416).
export const AlertOutlet = ({ className, inPage = false }) => {
    // Tolerate rendering outside the provider (isolated component tests).
    const ctx = useContext(AlertContext);
    const { alert, setAlert, hasPageOutlet, registerPageOutlet } = ctx || {};

    useEffect(() => {
        if (inPage && registerPageOutlet) return registerPageOutlet();
        return undefined;
    }, [inPage, registerPageOutlet]);

    if (!alert?.visible) return null;
    if (!inPage && hasPageOutlet) return null;
    return (
        <div className={className}>
            <Alert alert={alert} setAlert={setAlert} />
        </div>
    );
};
